import React, { useMemo } from "react";

// MUI
import { Box, Grid } from "@mui/material";

// Components
import ComboBox from "../../ComboBox";
import TextBox from "../../TextBox";
import CustomDatePicker from "../../DatePicker";

// Utils
import {
  createAutocompleteOptions,
  getDecimalValue,
  yesOrNoAutoCompleteOptions,
} from "../../../utility/utils";

// Config
import {
  tenderMaturityOptions,
  priceVariationOptions,
} from "../../../utility/config";

// Hooks
import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux/hooks/redux-hooks";

// Actions
import {
  setAssessCustomerRating,
  setBriefScope,
  setCompetitionIntensity,
  setEnvironmentClearanceStatus,
  setEstimatedTimeToBid,
  setOAndM,
  setOMDuration,
  setPreBidExpenses,
  setPreQualification,
  setPreQualificationRating,
  setPriceVariance,
  setProjectDuration,
  setRowStatus,
  setTenderMaturity,
  setTenderingBandwidth,
  setTenderExpectedDate,
  setAwardExpectedDate,
  setExpectedCompetitors,
  setProposalStage,
  setCompetitorsWithClient,
  setWinStrategyAndValueProposition,
  setTimeToPQ,
} from "../../../redux/actions/ProjectActions";

function GoNoGoSection() {
  const dispatch = useAppDispatch();

  const get_Project_Object = useAppSelector(
    (state) => state.project.project_Object
  );

  const all_TenderingBandwidths = useAppSelector(
    (state) => state.tenderingBandwidth.all_TenderingBandwidths
  );

  const all_CompetitionIntensities = useAppSelector(
    (state) => state.competitionIntensity.all_CompetitionIntensities
  );

  const tenderingBandwithOptions = useMemo(() => {
    return createAutocompleteOptions(
      all_TenderingBandwidths,
      "tenderingBandwidthId",
      "name"
    );
  }, [all_TenderingBandwidths]);

  const competitionIntensitiesOptions = useMemo(() => {
    return createAutocompleteOptions(
      all_CompetitionIntensities,
      "competitionIntensityId",
      "name"
    );
  }, [all_CompetitionIntensities]);

  const onChangeTextDataLengthForBriefScope = (val: string) => {
    dispatch(setBriefScope(val));
  };

  const onChangePrebidExpenses = (val: string) => {
    let value = getDecimalValue(val);
    dispatch(
      setPreBidExpenses(!value ? get_Project_Object.preBidExpenses : value)
    );
    // if (!Number.isNaN(parseInt(val))) {
    //   dispatch(setPreBidExpenses(parseInt(val)));
    // } else {
    //   dispatch(setPreBidExpenses(0));
    // }
  };

  const handlePriceVarianceOnChange = (value: string | number) => {
    if (typeof value === "number") {
      value = value.toString();
    }
    dispatch(setPriceVariance(value));
  };

  const onChangeAssessCustomerRatingOnChange = (val: string): void => {
    dispatch(setAssessCustomerRating(val));
  };

  const handlePQRequirementOnChange = (value: string | number) => {
    if (typeof value === "number") {
      value = value.toString();
    }
    //if (value !== "Yes") {
    dispatch(setPreQualificationRating(""));
    //}
    dispatch(setPreQualification(value));
  };

  const onChangeEnvClearance = (val: string) => {
    dispatch(setEnvironmentClearanceStatus(val));
  };

  const onChangeRowStatus = (val: string) => {
    dispatch(setRowStatus(val));
  };

  const onChangeProjectDuration = (val: string) => {
    if (!Number.isNaN(parseInt(val))) {
      dispatch(setProjectDuration(parseInt(val)));
    } else {
      dispatch(setProjectDuration(0));
    }
  };

  const onChangeEstimatedTimeToBid = (val: string) => {
    if (!Number.isNaN(parseInt(val))) {
      dispatch(setEstimatedTimeToBid(parseInt(val)));
    } else {
      dispatch(setEstimatedTimeToBid(0));
    }
  };

  const onChangeCompetitorsWithClient = (val: string) => {
    if (typeof val === "string") dispatch(setCompetitorsWithClient(val));
  };

  const onChangewinStrategyAndValueProposition = (val: string) => {
    if (typeof val === "string")
      dispatch(setWinStrategyAndValueProposition(val));
  };

  const onChangeTimeToPQ = (val: string) => {
    if (!Number.isNaN(parseInt(val))) {
      dispatch(setTimeToPQ(parseInt(val)));
    } else {
      dispatch(setTimeToPQ(0));
    }
  };

  const handleOAndMOnChange = (value: string | number) => {
    if (typeof value === "number") {
      value = value.toString();
    }
    if (value !== "1") {
      dispatch(setOMDuration(0));
    }
    dispatch(setOAndM(value === "1" ? true : false));
  };

  const onChangeOMDuration = (val: string) => {
    if (!Number.isNaN(parseInt(val))) {
      dispatch(setOMDuration(parseInt(val)));
    } else {
      dispatch(setOMDuration(0));
    }
  };

  const handleTenderingBandwidthOnChange = (value: string | number) => {
    if (typeof value === "string") {
      value = parseInt(value, 10);
    }
    dispatch(setTenderingBandwidth(value));
  };

  const onChangeTenderMaturity = (value: string | number) => {
    if (typeof value === "number") {
      value = value.toString();
    }
    dispatch(setTenderMaturity(value));
  };

  const handleCompIntensityOnChange = (value: string | number) => {
    if (typeof value === "string") {
      value = parseInt(value, 10);
    }
    dispatch(setCompetitionIntensity(value));
  };

  const handleProposalStageOnChange = (value: string | number) => {
    if (typeof value === "string") {
      dispatch(setProposalStage(value));
    }
  };

  const handleTenderExpectedDate = (date: Date | null): void => {
    if (date) {
      dispatch(setTenderExpectedDate(date));
    }
  };

  const handleAwardExpectedDate = (date: Date | null): void => {
    if (date) {
      dispatch(setAwardExpectedDate(date));
    }
  };

  const onChangeExpectedCompetitors = (val: string) => {
    dispatch(setExpectedCompetitors(val));
  };
  const ProposalStageOptions = [
    {
      value: "Single Stage (PQ & Price together)",
      label: "Single Stage (PQ & Price together)",
    },
    {
      value: "Two Stage (PQ/Technical first then price)",
      label: "Two Stage (PQ/Technical first then price)",
    },
  ];

  return (
    <Box
      sx={{
        padding: "1em",
        border: "1px solid rgb(0 0 0 / 18%)",
        borderRadius: "0.375rem",
      }}
    >
      <Grid container spacing={2} alignItems="start">
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            placeholder="Select PQ Requirement"
            label="TPL Meets PQ Requirements?"
            options={priceVariationOptions}
            value={get_Project_Object.isPqrequired}
            required={true}
            onChange={handlePQRequirementOnChange}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextBox
            changeHandler={onChangePrebidExpenses}
            label="Pre-Bid Expenses (INR Lac)"
            size="small"
            type="number"
            fullWidth
            value={get_Project_Object.preBidExpenses || 0}
            required={true}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            placeholder="Select Price Variation"
            label="Price Variation"
            info="Will or do the tender conditions include Price Variation"
            options={priceVariationOptions}
            value={get_Project_Object.isPriceVariation}
            required={true}
            onChange={handlePriceVarianceOnChange}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            options={tenderMaturityOptions}
            value={get_Project_Object.tenderMaturity}
            placeholder="Select Tender Maturity"
            label="Tender Maturity"
            onChange={onChangeTenderMaturity}
            required={true}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <CustomDatePicker
            label="Tender expected Date"
            placeholderText="Select a date..."
            required
            selected={get_Project_Object.tenderExpectedDate}
            onChange={handleTenderExpectedDate}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <CustomDatePicker
            label="Award expected Date"
            placeholderText="Select a date..."
            required
            selected={get_Project_Object.awardExpectedDate}
            onChange={handleAwardExpectedDate}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            placeholder="Select Tendering bandwidth"
            label="Tendering bandwidth"
            info="To be filled in only for Bids with NIT post consutaion with T&E Head"
            options={tenderingBandwithOptions}
            value={get_Project_Object.tenderingBandwidthId}
            onChange={handleTenderingBandwidthOnChange}
            required={true}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            placeholder="Select Competition Intensity"
            label="Competition Intensity"
            options={competitionIntensitiesOptions}
            required={true}
            value={get_Project_Object.competitionIntensityId}
            onChange={handleCompIntensityOnChange}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            placeholder="Select Proposal Stage"
            label="Proposal Stage"
            options={ProposalStageOptions}
            required={true}
            value={get_Project_Object.proposalStage}
            onChange={handleProposalStageOnChange}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextBox
            label="Competitors executing work with client"
            info="Please mention the competitor name if applicable"
            placeholder="Competitors executing work with client"
            value={get_Project_Object.competitorsWithClient}
            fullWidth
            size="small"
            changeHandler={onChangeCompetitorsWithClient}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextBox
            label="Win strategy and value proposition"
            placeholder="Win strategy and value proposition"
            value={get_Project_Object.winStrategyAndValueProposition}
            fullWidth
            size="small"
            changeHandler={onChangewinStrategyAndValueProposition}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextBox
            label="Estimated Time To Bid (Days)"
            info="Duration from NIT Date till Bid Submission Date"
            value={get_Project_Object.estimatedTimeToBid || 0}
            fullWidth
            size="small"
            changeHandler={onChangeEstimatedTimeToBid}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <TextBox
            label="Time to PQ (Days)"
            info="In case of 2 Stage bidding, no. of days available to submit PQ/Technical bid"
            value={get_Project_Object.timeToPQDays || 0}
            fullWidth
            size="small"
            changeHandler={onChangeTimeToPQ}
          />
        </Grid>

        <Grid item xs={12} sm={4} md={3}>
          <TextBox
            changeHandler={onChangeAssessCustomerRatingOnChange}
            label="Assess customer rating"
            info="Credit Rating of Client and Credit  Agency Name. Must for Non-Green Customers."
            placeholder="Assess customer rating"
            inputProps={{ maxLength: 200 }}
            size="small"
            fullWidth
            value={get_Project_Object.assessCustomerRating || ""}
          />
        </Grid>
        <Grid item xs={12} sm={4} md={3}>
          <ComboBox
            placeholder="Select O & M part of scope"
            label="O & M part of scope?"
            options={yesOrNoAutoCompleteOptions}
            value={
              get_Project_Object.isOM === true
                ? yesOrNoAutoCompleteOptions[1].value
                : yesOrNoAutoCompleteOptions[0].value
            }
            required={true}
            onChange={handleOAndMOnChange}
          />
        </Grid>
        {get_Project_Object.isOM === true && (
          <Grid item xs={12} sm={4} md={3}>
            <TextBox
              label="O&M Duration (In months)"
              value={get_Project_Object.omduration || 0}
              changeHandler={onChangeOMDuration}
              fullWidth
              required
              size="small"
            />
          </Grid>
        )}
        <Grid item xs={12} sm={4} md={3}>
          <TextBox
            label="Project Duration In Months (excluding O&M)"
            value={get_Project_Object.projectDuration || 0}
            changeHandler={onChangeProjectDuration}
            fullWidth
            size="small"
          />
        </Grid>
        <Grid item xs={12}>
          <TextBox
            changeHandler={onChangeTextDataLengthForBriefScope}
            label="Brief scope"
            info="Please briefly enter the scope of work for the Project"
            placeholder="Please briefly enter the scope of work for the Project"
            multiline
            maxRows={3}
            variant="standard"
            required
            fullWidth
            value={get_Project_Object.briefScope || ""}
          />
        </Grid>

        <Grid item xs={12}>
          <TextBox
            label="Environment clearance Status"
            info="Is MOEF Clearance required for project. If Yes, please elaborate on Status and mention contractors' scope for any statutory approvals."
            placeholder="Enter Environment clearance Status"
            variant="standard"
            multiline
            maxRows={3}
            fullWidth
            value={get_Project_Object.environmentClearanceStatus || ""}
            changeHandler={onChangeEnvClearance}
          />
        </Grid>
        <Grid item xs={12}>
          <TextBox
            label="ROW Status"
            info="Is RoW required. If Yes, please elaborate on current status and mention contractors' scope pertaining to ROW."
            placeholder="Enter ROW Status"
            variant="standard"
            multiline
            maxRows={3}
            fullWidth
            value={get_Project_Object.rowstatus || ""}
            changeHandler={onChangeRowStatus}
          />
        </Grid>

        <Grid item xs={12}>
          <TextBox
            label="Expected Competitors"
            info="Please list names of competitors expected to bid for the project"
            placeholder="Please list names of competitors expected to bid for the project"
            required
            fullWidth
            multiline
            maxRows={3}
            variant="standard"
            value={get_Project_Object.expectedCompetitors || ""}
            changeHandler={onChangeExpectedCompetitors}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default GoNoGoSection;
